import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { dashboardApi, benchmarkingApi } from '../lib/api';
import type { SavingsDashboard } from '../types';
import AppShell from '../components/AppShell';
import UploadVendorDataModal from '../components/UploadVendorDataModal';
import { useTenantContext } from '../context/TenantContext';

const SANS = "'Roboto', system-ui, sans-serif";

export default function SavingsDashboardPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<SavingsDashboard | null>(null);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isSuperAdmin = user?.role === 'super_admin';
  const { tenants, selectedTenantId, selectedTenant } = useTenantContext();
  const [showUpload, setShowUpload] = useState(false);

  useEffect(() => {
    if (!user) return;
    const allowed = ['cfo', 'procurement_head', 'procurement_manager', 'procurement_executive', 'super_admin'];
    if (!allowed.includes(user.role)) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  useEffect(() => {
    if (!user) return;
    if (isSuperAdmin && selectedTenantId === undefined) return;
    setFetching(true);
    dashboardApi
      .savings(isSuperAdmin ? selectedTenantId : undefined)
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setFetching(false));
  }, [user, isSuperAdmin, selectedTenantId]);

  if (loading || !user) return null;

  const subtitle = isSuperAdmin && selectedTenant
    ? `Viewing: ${selectedTenant.name}`
    : 'CFO view — assessed spend, identified savings, upcoming renewals';

  const actions = isSuperAdmin ? (
    <button
      onClick={() => setShowUpload(true)}
      style={{
        backgroundColor: '#e0f2fe', border: '1px solid rgba(14,165,233,0.3)',
        borderRadius: 8, padding: '0.35rem 0.85rem',
        color: '#0369a1', fontSize: '0.82rem', cursor: 'pointer', whiteSpace: 'nowrap',
        fontFamily: SANS,
      }}
    >
      Upload Vendor Data
    </button>
  ) : undefined;

  return (
    <AppShell user={user} title="Savings Dashboard" subtitle={subtitle} actions={actions}>
      {error && <ErrorBanner message={error} />}

      {fetching ? (
        <p style={{ color: '#94a3b8', fontSize: '0.9rem' }}>Loading…</p>
      ) : data ? (
        <Content data={data} />
      ) : null}

      {showUpload && (
        <SavingsUploadWrapper
          tenants={tenants}
          defaultTenantId={selectedTenantId}
          onClose={() => setShowUpload(false)}
        />
      )}
    </AppShell>
  );
}

/**
 * Legacy wrapper: keeps the tenant-picker + "Run Benchmarking" cta flow that
 * the savings dashboard used before modal extraction. The new onboarding
 * page uses `UploadVendorDataModal` directly (no tenant picker).
 */
function SavingsUploadWrapper({
  tenants, defaultTenantId, onClose,
}: {
  tenants: Array<{ id: string; name: string }>;
  defaultTenantId: string | undefined;
  onClose: () => void;
}) {
  const [targetTenantId, setTargetTenantId] = useState(defaultTenantId ?? tenants[0]?.id ?? '');
  const [benchmarkRunning, setBenchmarkRunning] = useState(false);
  const [benchmarkMsg, setBenchmarkMsg] = useState<string | null>(null);
  const [uploadDone, setUploadDone] = useState(false);
  const selectedTenant = tenants.find((t) => t.id === targetTenantId);

  const runBenchmarking = async () => {
    if (!targetTenantId) return;
    setBenchmarkRunning(true); setBenchmarkMsg(null);
    try {
      await benchmarkingApi.run(targetTenantId);
      setBenchmarkMsg('Benchmarking started. Results will appear in the Savings Dashboard shortly.');
    } catch (err) {
      setBenchmarkMsg(err instanceof Error ? err.message : 'Failed to start benchmarking');
    } finally { setBenchmarkRunning(false); }
  };

  if (uploadDone) {
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 200, backgroundColor: 'rgba(15,23,42,0.4)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
           onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        <div style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 14, padding: '2rem', width: '100%', maxWidth: 480, fontFamily: SANS, boxShadow: '0 8px 24px rgba(0,0,0,0.10)' }}>
          <p style={{ margin: '0 0 0.75rem', fontSize: '1rem', color: '#0f172a' }}>Upload successful for {selectedTenant?.name ?? 'tenant'}.</p>
          <p style={{ margin: '0 0 1.25rem', fontSize: '0.82rem', color: '#64748b' }}>Run benchmarking now to identify savings opportunities, or close this dialog.</p>
          {benchmarkMsg ? (
            <p style={{ margin: '0 0 0.75rem', fontSize: '0.82rem', color: benchmarkMsg.startsWith('Benchmark') ? '#065f46' : '#991b1b' }}>{benchmarkMsg}</p>
          ) : (
            <button onClick={runBenchmarking} disabled={benchmarkRunning}
              style={{ width: '100%', padding: '0.65rem', borderRadius: 8, border: 'none', backgroundColor: benchmarkRunning ? '#cbd5e1' : '#0ea5e9', color: benchmarkRunning ? '#94a3b8' : '#ffffff', fontSize: '0.875rem', fontWeight: 400, cursor: benchmarkRunning ? 'not-allowed' : 'pointer', marginBottom: '0.75rem', fontFamily: SANS }}>
              {benchmarkRunning ? 'Starting…' : 'Run Benchmarking'}
            </button>
          )}
          <button onClick={onClose} style={{ width: '100%', padding: '0.55rem', borderRadius: 8, border: '1px solid #e2e8f0', background: 'none', color: '#64748b', fontSize: '0.82rem', cursor: 'pointer', fontFamily: SANS }}>
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <UploadVendorDataModal
      tenantId={targetTenantId}
      tenantName={selectedTenant?.name}
      tenantPicker={{ tenants, selectedId: targetTenantId, onChange: setTargetTenantId }}
      onClose={onClose}
      onSuccess={() => setUploadDone(true)}
    />
  );
}

function Content({ data }: { data: SavingsDashboard }) {
  const run = data.latestRun;
  const hasRun = run != null && ['completed', 'adjusted', 'locked'].includes(run.status);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.75rem' }}>
      {/* Stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: '1rem' }}>
        <StatCard
          label="Total Assessed Spend"
          value={formatCurrencyLarge(data.totalAssessedAcv)}
          sub="Annual contract value"
          accent="#0ea5e9"
        />
        <StatCard
          label="Identified Savings"
          value={hasRun && run!.savingsLow != null && run!.savingsHigh != null
            ? `${formatCurrencyLarge(run!.savingsLow)} – ${formatCurrencyLarge(run!.savingsHigh)}`
            : '—'}
          sub={hasRun ? 'Low–high range · 70–100% of potential' : 'Run benchmarking to see savings'}
          accent={hasRun ? '#10b981' : '#94a3b8'}
        />
        <StatCard
          label="Vendors Assessed"
          value={hasRun ? `${run!.completedCount} / ${run!.vendorCount}` : '—'}
          sub={hasRun ? `Last run ${formatRelativeDate(run!.createdAt)}` : 'No benchmark run yet'}
          accent="#8b5cf6"
        />
        <StatCard
          label="Renewals in 90 Days"
          value={data.upcomingRenewals.length > 0 ? String(data.upcomingRenewals.length) : '0'}
          sub={data.upcomingRenewals.length > 0
            ? formatCurrencyLarge(data.upcomingRenewals.reduce((s, r) => s + (r.annualContractValue ?? 0), 0)) + ' at stake'
            : 'No urgent renewals'}
          accent={data.upcomingRenewals.length > 0 ? '#ef4444' : '#94a3b8'}
        />
      </div>

      {!hasRun && (
        <div style={{
          backgroundColor: '#e0f2fe', border: '1px solid rgba(14,165,233,0.3)',
          borderRadius: 10, padding: '1rem 1.5rem', fontSize: '0.85rem', color: '#0369a1',
        }}>
          {run == null
            ? 'No benchmarking run has been triggered yet. A SpendLens administrator can run benchmarking from the admin panel.'
            : `Latest run status: ${run.status}. Benchmarking is still in progress.`}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: '1rem' }}>
        <section style={card}>
          <SectionTitle>Top Vendors to Renegotiate</SectionTitle>
          {data.topVendors.length === 0 ? (
            <p style={{ color: '#64748b', fontSize: '0.85rem', margin: '0.75rem 0 0' }}>
              {hasRun ? 'All vendors appear to be at market rates.' : 'Run benchmarking to identify savings opportunities.'}
            </p>
          ) : (
            <div>
              <div style={rowHeader}>
                <span style={{ flex: '2' }}>Vendor</span>
                <span style={{ flex: '1', textAlign: 'right' }}>Current ACV</span>
                <span style={{ flex: '1', textAlign: 'right' }}>Savings</span>
                <span style={{ flex: '0.8', textAlign: 'center' }}>Over-market</span>
              </div>
              {data.topVendors.map((v, i) => (
                <div key={v.vendorId ?? i} style={{ ...rowStyle, borderTop: '1px solid #e2e8f0' }}>
                  <div style={{ flex: '2' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <RankBadge rank={i + 1} />
                      <div>
                        <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: 400, color: '#0f172a' }}>{v.vendorName}</p>
                        {v.product && <p style={{ margin: 0, fontSize: '0.72rem', color: '#94a3b8' }}>{v.product}</p>}
                      </div>
                    </div>
                  </div>
                  <div style={{ flex: '1', textAlign: 'right' }}>
                    <span style={{ fontSize: '0.85rem', color: '#94a3b8' }}>
                      {v.currentAcv != null ? formatCurrencyLarge(v.currentAcv) : '—'}
                    </span>
                  </div>
                  <div style={{ flex: '1', textAlign: 'right' }}>
                    <span style={{ fontSize: '0.875rem', fontWeight: 400, color: '#10b981' }}>
                      {formatCurrencyLarge(v.savingsPotentialUsd)}
                    </span>
                  </div>
                  <div style={{ flex: '0.8', textAlign: 'center' }}>
                    {v.overpaymentPct != null ? (
                      <span style={{ fontSize: '0.78rem', fontWeight: 400, color: v.overpaymentPct > 25 ? '#ef4444' : v.overpaymentPct > 10 ? '#f59e0b' : '#10b981' }}>
                        +{v.overpaymentPct.toFixed(0)}%
                      </span>
                    ) : '—'}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <section style={card}>
            <SectionTitle>Spend by Category</SectionTitle>
            {data.spendByCategory.length === 0 ? (
              <p style={{ color: '#64748b', fontSize: '0.85rem', margin: '0.75rem 0 0' }}>No spend data yet.</p>
            ) : (
              <CategoryChart categories={data.spendByCategory} total={data.totalAssessedAcv} />
            )}
          </section>

          <section style={card}>
            <SectionTitle>Upcoming Renewals (90 days)</SectionTitle>
            {data.upcomingRenewals.length === 0 ? (
              <p style={{ color: '#64748b', fontSize: '0.85rem', margin: '0.75rem 0 0' }}>
                No contracts renewing in the next 90 days.
              </p>
            ) : (
              <div>
                {data.upcomingRenewals.map((r) => {
                  const urgentColor = r.daysUntilRenewal <= 30 ? '#ef4444' : r.daysUntilRenewal <= 60 ? '#f59e0b' : '#64748b';
                  return (
                    <div key={r.id} style={{ ...rowStyle, borderTop: '1px solid #e2e8f0', alignItems: 'center' }}>
                      <div style={{ flex: '1' }}>
                        <p style={{ margin: 0, fontSize: '0.85rem', fontWeight: 400, color: '#0f172a' }}>{r.name}</p>
                        <p style={{ margin: 0, fontSize: '0.72rem', color: '#94a3b8' }}>{formatDate(r.renewalDate)}</p>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <p style={{ margin: 0, fontSize: '0.78rem', fontWeight: 400, color: urgentColor }}>
                          {r.daysUntilRenewal === 0 ? 'Today' : `${r.daysUntilRenewal}d`}
                        </p>
                        {r.annualContractValue != null && (
                          <p style={{ margin: 0, fontSize: '0.72rem', color: '#94a3b8' }}>
                            {formatCurrencyLarge(r.annualContractValue)}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

function CategoryChart({ categories, total }: { categories: { category: string; totalAcv: number; vendorCount: number }[]; total: number }) {
  const max = Math.max(...categories.map((c) => c.totalAcv));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginTop: '0.75rem' }}>
      {categories.map((c) => {
        const pct = total > 0 ? (c.totalAcv / total) * 100 : 0;
        const barWidth = max > 0 ? (c.totalAcv / max) * 100 : 0;
        return (
          <div key={c.category}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.2rem' }}>
              <span style={{ fontSize: '0.75rem', color: '#475569', textTransform: 'capitalize' }}>{c.category.replace('_', ' ')}</span>
              <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{formatCurrencyLarge(c.totalAcv)} · {pct.toFixed(0)}%</span>
            </div>
            <div style={{ height: '5px', backgroundColor: '#f1f5f9', borderRadius: '3px', overflow: 'hidden' }}>
              <div style={{ height: '100%', borderRadius: '3px', width: `${barWidth}%`, backgroundColor: '#0ea5e9', transition: 'width 0.4s ease' }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function StatCard({ label, value, sub, accent }: { label: string; value: string; sub: string; accent: string }) {
  return (
    <div style={{
      backgroundColor: '#ffffff',
      border: '1px solid #e2e8f0',
      borderTop: `3px solid ${accent}`,
      borderRadius: 10,
      padding: '1.25rem 1.5rem',
      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    }}>
      <p style={{ margin: '0 0 0.5rem', fontSize: '0.7rem', fontWeight: 500, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</p>
      <p style={{ margin: '0 0 0.35rem', fontSize: '1.45rem', fontWeight: 300, color: '#0f172a', letterSpacing: '-0.02em', lineHeight: 1.1 }}>{value}</p>
      <p style={{ margin: 0, fontSize: '0.72rem', color: '#94a3b8' }}>{sub}</p>
    </div>
  );
}

function RankBadge({ rank }: { rank: number }) {
  return (
    <span style={{
      width: '20px', height: '20px', borderRadius: '50%', flexShrink: 0,
      backgroundColor: '#e0f2fe', color: '#0ea5e9',
      fontSize: '0.7rem', fontWeight: 500,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>{rank}</span>
  );
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <p style={{ margin: '0 0 0.5rem', fontSize: '0.7rem', fontWeight: 500, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
      {children}
    </p>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div style={{
      backgroundColor: '#fee2e2', border: '1px solid rgba(239,68,68,0.3)',
      borderRadius: 8, padding: '0.75rem 1rem', color: '#991b1b',
      fontSize: '0.85rem', marginBottom: '1.25rem',
    }}>
      {message}
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────
function formatCurrencyLarge(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatRelativeDate(isoStr: string): string {
  const days = Math.floor((Date.now() - new Date(isoStr).getTime()) / (1000 * 60 * 60 * 24));
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  return `${days} days ago`;
}

// ── Styles ─────────────────────────────────────────────────────────
const card: React.CSSProperties = {
  backgroundColor: '#ffffff', border: '1px solid #e2e8f0',
  borderRadius: 10, padding: '1.25rem 1.5rem',
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
};

const rowHeader: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: '1rem',
  fontSize: '0.65rem', fontWeight: 500, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em',
  paddingBottom: '0.5rem', marginBottom: '0.25rem',
};

const rowStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.6rem 0',
};

